// FTEDN — Beta program store (Zustand)
import { create } from 'zustand';
import { apiClient } from '../hooks/useApi';

export interface BetaProgram {
  id: string;
  name: string;
  description: string;
  status: 'accepting' | 'testing' | 'completed';
  spotsLeft: number;
  xpReward: number;
  phase: number;
  totalPhases: number;
  productId?: string;
  createdAt?: string;
}

export interface BetaApplication {
  id: string;
  programId: string;
  userId: string;
  status: 'pending' | 'accepted' | 'rejected' | 'completed';
  appliedAt: string;
  shippingAddress?: string;
  shipment?: Record<string, unknown> | null;
}

interface BetaState {
  programs: BetaProgram[];
  applications: BetaApplication[];
  loading: boolean;
  error: string | null;
  fetchPrograms: () => Promise<void>;
  fetchMyApplications: () => Promise<void>;
  applyToProgram: (programId: string, shippingAddress?: string) => Promise<boolean>;
}

/** Map raw Firestore beta program doc */
export function mapProgram(raw: Record<string, unknown>): BetaProgram {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? 'Untitled'),
    description: String(raw.description ?? ''),
    status: (raw.status as BetaProgram['status']) ?? 'accepting',
    spotsLeft: Number(raw.spotsLeft ?? raw.spots ?? 0),
    xpReward: Number(raw.xpReward ?? raw.xp ?? 0),
    phase: Number(raw.phase ?? 1),
    totalPhases: Number(raw.totalPhases ?? 4),
    productId: raw.productId as string | undefined,
    createdAt: raw.createdAt as string | undefined,
  };
}

/** Map raw Firestore application doc */
export function mapApplication(raw: Record<string, unknown>): BetaApplication {
  return {
    id: String(raw.id ?? ''),
    programId: String(raw.programId ?? ''),
    userId: String(raw.userId ?? ''),
    status: (raw.status as BetaApplication['status']) ?? 'pending',
    appliedAt: String(raw.appliedAt ?? ''),
    shippingAddress: raw.shippingAddress as string | undefined,
    shipment: (raw.shipment as Record<string, unknown> | null | undefined) ?? null,
  };
}

export const useBetaStore = create<BetaState>((set) => ({
  programs: [],
  applications: [],
  loading: false,
  error: null,

  fetchPrograms: async () => {
    set({ loading: true, error: null });
    try {
      const res = await apiClient.get('/beta/programs');
      if (res.success && Array.isArray(res.data)) {
        set({ programs: res.data.map(mapProgram), loading: false });
      } else {
        set({ error: 'Failed to load programs', loading: false });
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchMyApplications: async () => {
    try {
      const res = await apiClient.get('/beta/my-applications');
      if (res.success && Array.isArray(res.data)) {
        set({ applications: res.data.map(mapApplication) });
      }
    } catch { /* silent — non-critical */ }
  },

  applyToProgram: async (programId, shippingAddress) => {
    try {
      const res = await apiClient.post('/beta/apply', { programId, shippingAddress });
      return res.success === true;
    } catch {
      return false;
    }
  },
}));
